import React, { memo, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Stack,
  Text,
  Badge,
  useColorModeValue,
  Switch,
  Image,
  Collapse,
  useBreakpoint,
  VStack,
} from '@chakra-ui/react';
import { Token } from '@/types/wallet';
import { formatUSD, formatTokenAmount } from '../../../../utils/format';
import { Typography } from '@/kit/antipad-ui';

// Иконка по умолчанию для токенов с битыми изображениями
const DEFAULT_TOKEN_ICON = "https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/svg/color/generic.svg";

interface TokenCardProps {
  token: Token;
  onToggle: () => void;
  purchaseStrategy?: 'full' | 'proportional' | 'equal';
  calculatedAmount?: {
    usd: number;
    tokens: number;
  };
}

export const TokenCard: React.FC<TokenCardProps> = memo(({
  token,
  onToggle,
  purchaseStrategy,
  calculatedAmount
}) => {
  const borderColor = 'rgba(60, 60, 60, 0.47)';
  const selectedBg = 'rgba(68, 255, 168, 0.05)';
  const selectedBorder = 'rgba(68, 255, 168, 0.2)';
  const badgeBg = useColorModeValue('gray.100', 'gray.700');
  const warningColor = useColorModeValue('orange.500', 'orange.300');
  const [imgError, setImgError] = useState(false);
  const screen = useBreakpoint();
  const isMobile = screen === 'base' || screen === 'sm';
  // Обработчик ошибки загрузки изображения
  const handleImageError = () => {
    setImgError(true);
  };

  const showCalculated = token.selected && purchaseStrategy !== 'full' && calculatedAmount !== undefined;

  // Проверяем, будет ли сумма меньше минимальной
  const getAmountToCheck = () => {
    if (!token.selected) return null;

    switch (purchaseStrategy) {
      case 'full':
        return token.usdValue;
      case 'proportional':
      case 'equal':
        return calculatedAmount?.usd || 0;
      default:
        return token.usdValue;
    }
  };

  const amountToCheck = getAmountToCheck();
  const isTooSmall = amountToCheck !== null && amountToCheck < 0.1;
  const noPool = token.hasPool === false;


  if (isMobile) {
    return (
      <Box
        p={4}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={token.selected ? (isTooSmall ? warningColor : selectedBorder) : borderColor}
        bg={token.selected ? selectedBg : 'transparent'}
        transition="all 0.2s"
        overflow="hidden"
        _hover={{
          boxShadow: 'md',
          transform: 'translateY(-1px)',
        }}
      >
        <Flex direction="column" justify="space-between" align="center">
          <HStack mb={2} justify="start" spacing={4} w="100%">
            <Box
              boxSize="40px"
              borderRadius="full"
              overflow="hidden"
              bg="gray.100"
              p="2px"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
              display="flex"
              alignItems="center"
              flexShrink={0}
              justifyContent="center"
            >
              <Image
                src={imgError ? DEFAULT_TOKEN_ICON : token.logoUrl || token.icon || DEFAULT_TOKEN_ICON}
                alt={token.name}
                fallback={<Box bg="gray.200" borderRadius="full" boxSize="36px" />}
                onError={handleImageError}
                boxSize="36px"
              />
            </Box>
            <Stack spacing={0}>
              <VStack align="start" spacing={2}>
                <Typography>{token.name}</Typography>
                <Badge
                  bg={badgeBg}
                  color="gray.500"
                  borderRadius="md"
                  px={2}
                  mb={2}
                  fontSize="xs"
                >
                  <Typography variant='small_s'>{token.symbol}</Typography>
                </Badge>
                {isTooSmall && (
                  <Typography variant='small_s' color='#F6AD55'>Amount too small (min $0.1)</Typography>
                )}
                {noPool && (
                  <Typography variant='small_s' color='#F6AD55'>Token Not Recommended</Typography>
                )}
              </VStack>
              <Typography variant='small' color='var(--color-content-tertiary)'>
                {formatTokenAmount(token.amount, token.symbol)}
                {showCalculated && (
                  <Text as="span" color={isTooSmall ? warningColor : "blue.400"} ml={2}>
                    → {formatTokenAmount(calculatedAmount?.tokens || 0, token.symbol)}
                  </Text>
                )}
              </Typography>
            </Stack>
          </HStack>

          <VStack align="end" spacing={1} w="100%">
            <Stack spacing={0} align="flex-end">
              <Typography
                variant='medium'
                color={isTooSmall ? warningColor : (token.usdValue >= 10 ? "#44FFA8" : "#F6AD55")}
              >
                {formatUSD(token.usdValue)}
              </Typography>
              {showCalculated && (
                <Text fontSize="sm" color={isTooSmall ? warningColor : "blue.400"}>
                  {formatUSD(calculatedAmount?.usd || 0)}
                </Text>
              )}
            </Stack>
            <Switch
              colorScheme='teal'
              size="md"
              isChecked={token.selected}
              onChange={onToggle}
            />
          </VStack>
        </Flex>
      </Box>
    )
  }
  return (
    <Box
      p={4}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={token.selected ? (isTooSmall ? warningColor : selectedBorder) : borderColor}
      bg={token.selected ? selectedBg : 'transparent'}
      transition="all 0.2s"
      overflow="hidden"
      _hover={{
        boxShadow: 'md',
        transform: 'translateY(-1px)',
      }}
    >
      <Flex justify="space-between" align="center">
        <HStack spacing={4}>
          <Box
            boxSize="40px"
            borderRadius="full"
            overflow="hidden"
            bg="gray.100"
            p="2px"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            display="flex"
            alignItems="center"
            flexShrink={0}
            justifyContent="center"
          >
            <Image
              src={imgError ? DEFAULT_TOKEN_ICON : token.logoUrl || token.icon || DEFAULT_TOKEN_ICON}
              alt={token.name}
              fallback={<Box bg="gray.200" borderRadius="full" boxSize="36px" />}
              onError={handleImageError}
              boxSize="36px"
            />
          </Box>
          <Stack spacing={0}>
            <HStack spacing={2}>
              <Typography>{token.name}</Typography>
              <Badge
                bg={badgeBg}
                color="gray.500"
                borderRadius="md"
                px={2}
                py={0.5}
                fontSize="xs"
              >
                <Typography variant='small_s'>{token.symbol}</Typography>
              </Badge>
              {isTooSmall && (
                <Typography variant='small_s' color='#F6AD55'>Amount too small (min $0.1)</Typography>
              )}
              {noPool && (
                <Typography variant='small_s' color='#F6AD55'>Token Not Recommended</Typography>
              )}
            </HStack>
            <Typography variant='small' color='var(--color-content-tertiary)'>
              {formatTokenAmount(token.amount, token.symbol)}
              {showCalculated && (
                <Text as="span" color={isTooSmall ? warningColor : "blue.400"} ml={2}>
                  → {formatTokenAmount(calculatedAmount?.tokens || 0, token.symbol)}
                </Text>
              )}
            </Typography>
          </Stack>
        </HStack>

        <HStack spacing={6}>
          <Stack spacing={0} align="flex-end">
            <Typography
              variant='medium'
              color={isTooSmall ? warningColor : (token.usdValue >= 10 ? "#44FFA8" : "#F6AD55")}
            >
              {formatUSD(token.usdValue)}
            </Typography>
            {showCalculated && (
              <Text fontSize="sm" color={isTooSmall ? warningColor : "blue.400"}>
                {formatUSD(calculatedAmount?.usd || 0)}
              </Text>
            )}
          </Stack>
          <Switch
            colorScheme='teal'
            size="md"
            isChecked={token.selected}
            onChange={onToggle}
          />
        </HStack>
      </Flex>
    </Box>
  );
}); 